/**
 * Shared cell renderers for the contract tables. Mirrors the legacy
 * `getContractColumns` intent (number-id link, service link, status pill,
 * role/direction chips, right-aligned money + dates) on `@vnetwork/ui`
 * primitives + `--vn-*` tokens. No hand-rolled pills/chips.
 */
import type { ReactNode } from 'react';
import { Badge } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { TableLink } from '@/components/table-link';
import { EntityLink } from '@/components/entity-link';
import { formatDate, formatMoney } from '@/lib/format';
import type { ContractRow } from '@/api/contracts';
import { contractStatusTone, statusLabel, roleLabel, directionLabel } from '../contract-format';

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;

/** Contract-number link → the contract detail route (primary text token). */
export function contractNumberCell(row: ContractRow): ReactNode {
  const label = row.contract_number_id || `#${row.contract_id}`;
  return (
    <TableLink to={`/contracts/${row.contract_id}`} title={label}>
      {label}
    </TableLink>
  );
}

/**
 * Customer: single-line `{id} – {name}`, linked to the customer detail.
 */
export function contractCustomerCell(row: ContractRow): ReactNode {
  if (!row.customer_id) return <span style={muted}>—</span>;
  return (
    <EntityLink
      to={`/customers/${row.customer_id}`}
      id={row.customer_id}
      name={row.company_name || row.customer_name}
    />
  );
}

/** Service from `services[0]`, single-line `{id} – {name}`, linked. */
export function serviceCell(row: ContractRow): ReactNode {
  const svc = row.services?.[0];
  if (!svc) return <span style={muted}>—</span>;
  return (
    <EntityLink
      to={`/services/${svc.service_id}`}
      id={svc.service_id}
      name={svc.service_name}
    />
  );
}

/** Status → `StatusPill` with the shared contract tone map. */
export function contractStatusCell(status?: string): ReactNode {
  return <StatusPill tone={contractStatusTone(status)}>{statusLabel(status)}</StatusPill>;
}

/** Contract role as a neutral `Badge`. */
export function roleCell(role?: string): ReactNode {
  if (!role) return <span style={muted}>—</span>;
  return <Badge tone="neutral">{roleLabel(role)}</Badge>;
}

/** Contract direction as a tinted `Badge` (earn=success/in, pay=warning/out). */
export function directionCell(direction?: string): ReactNode {
  if (!direction) return <span style={muted}>—</span>;
  const tone = direction === 'earn' ? 'success' : 'warning';
  return <Badge tone={tone}>{directionLabel(direction)}</Badge>;
}

/** Right-aligned VND money cell. */
export function moneyCell(value: number | null | undefined): ReactNode {
  return (
    <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
      {formatMoney(value)}
    </span>
  );
}

/** Right-aligned DD/MM/YYYY date cell. */
export function dateCell(iso?: string | null): ReactNode {
  return (
    <span style={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap' }}>
      {formatDate(iso)}
    </span>
  );
}

/** Plain text with an em-dash fallback + ellipsis. */
export function textCell(value?: string | null): ReactNode {
  if (!value) return <span style={muted}>—</span>;
  return (
    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={value}>
      {value}
    </span>
  );
}
